import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FamilyAccessDialogComponent } from '../family-access-dialog/family-access-dialog.component';
import { FamilyService } from '../../core/families/family.service';
import { FamilyJoinRequestDto, FamilySummaryDto } from '../../api/generated';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { ThemeService } from '../../core/theme/theme.service';

export type SettingsSection = 'account' | 'families' | 'preferences';

@Component({
  selector: 'app-settings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatChipsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSnackBarModule,
    MatProgressBarModule,
    MatSlideToggleModule
  ],
  templateUrl: './settings-dialog.component.html',
  styleUrls: ['./settings-dialog.component.scss']
})
export class SettingsDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<SettingsDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly data = inject<{ section?: SettingsSection } | null>(MAT_DIALOG_DATA, { optional: true });
  private readonly familyService = inject(FamilyService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly themeService = inject(ThemeService);

  readonly section = signal<SettingsSection>(this.data?.section ?? 'account');
  readonly expandedFamilyId = signal<string | null>(null);
  readonly joinRequests = signal<FamilyJoinRequestDto[]>([]);
  readonly joinRequestsLoading = signal(false);
  readonly requestRoleSelections = signal<Record<string, string>>({});
  readonly avacToggleLoading = signal<Record<string, boolean>>({});
  readonly budgetToggleLoading = signal<Record<string, boolean>>({});
  readonly recipeToggleLoading = signal<Record<string, boolean>>({});
  readonly sleepTrackingToggleLoading = signal<Record<string, boolean>>({});
  readonly familyRoleOptions = [
    { label: 'Parent', value: 'parent' },
    { label: 'Teen', value: 'teen' },
    { label: 'Kid', value: 'kid' }
  ];
  readonly sectionOptions: { label: string; value: SettingsSection; icon: string }[] = [
    { label: 'Account', value: 'account', icon: 'person' },
    { label: 'Families', value: 'families', icon: 'diversity_3' },
    { label: 'Preferences', value: 'preferences', icon: 'tune' }
  ];
  readonly isMobile = toSignal(
    this.breakpointObserver.observe(['(max-width: 768px)']).pipe(map((result) => result.matches)),
    { initialValue: false }
  );
  readonly usingDarkMode = this.themeService.usingDarkMode;

  constructor() {
    void this.familyService.refreshFamilies().catch(() => undefined);
  }

  toggleDarkMode(enabled: boolean) {
    this.themeService.toggleDarkMode(enabled);
  }

  get families() {
    return this.familyService.families();
  }

  get selectedFamilyId() {
    return this.familyService.selectedFamilyId();
  }

  switchSection(section: SettingsSection) {
    this.section.set(section);
  }

  selectFamily(id: string | null | undefined) {
    this.familyService.selectFamily(id);
  }

  openInviteDialog() {
    this.dialog.open(FamilyAccessDialogComponent, { width: '520px' });
  }

  toggleJoinRequests(family: FamilySummaryDto) {
    const familyId = family.id;
    if (!familyId) {
      return;
    }

    if (this.expandedFamilyId() === familyId) {
      this.expandedFamilyId.set(null);
      this.joinRequests.set([]);
      return;
    }

    this.expandedFamilyId.set(familyId);
    void this.loadJoinRequests(familyId);
  }

  roleLabel(role: string | null | undefined) {
    switch (role) {
      case 'parent':
        return 'Parent';
      case 'teen':
        return 'Teen';
      case 'kid':
        return 'Kid';
      default:
        return role;
    }
  }

  getRoleSelection(requestId: string | undefined) {
    if (!requestId) {
      return 'kid';
    }
    return this.requestRoleSelections()[requestId] ?? 'kid';
  }

  setRoleSelection(requestId: string | undefined, role: string) {
    if (!requestId) {
      return;
    }
    this.requestRoleSelections.update((current) => ({ ...current, [requestId]: role }));
  }

  async approveRequest(familyId: string | undefined, requestId: string | undefined) {
    if (!familyId || !requestId) {
      return;
    }
    this.joinRequestsLoading.set(true);
    try {
      await this.familyService.approveJoinRequest(familyId, requestId, this.getRoleSelection(requestId));
      this.snackBar.open('Request approved', undefined, { duration: 2500 });
      await this.loadJoinRequests(familyId);
    } catch (error) {
      this.joinRequestsLoading.set(false);
      this.snackBar.open((error as Error).message ?? 'Unable to approve request.', undefined, { duration: 3000 });
    }
  }

  async denyRequest(familyId: string | undefined, requestId: string | undefined) {
    if (!familyId || !requestId) {
      return;
    }
    this.joinRequestsLoading.set(true);
    try {
      await this.familyService.denyJoinRequest(familyId, requestId);
      this.snackBar.open('Request declined', undefined, { duration: 2500 });
      await this.loadJoinRequests(familyId);
    } catch (error) {
      this.joinRequestsLoading.set(false);
      this.snackBar.open((error as Error).message ?? 'Unable to decline request.', undefined, { duration: 3000 });
    }
  }

  isAvacToggleLoading(familyId: string | undefined) {
    if (!familyId) {
      return false;
    }
    return this.avacToggleLoading()[familyId] ?? false;
  }

  async toggleAvacSubmission(family: FamilySummaryDto, enabled: boolean) {
    if (!family.id) {
      return;
    }
    this.setAvacToggleLoading(family.id, true);
    try {
      await this.familyService.updateAvacSubmissionEnabled(family.id, enabled);
      this.snackBar.open(
        enabled ? 'AVAC submissions enabled for this family.' : 'AVAC submissions disabled.',
        undefined,
        { duration: 2500 }
      );
    } catch (error) {
      this.snackBar.open((error as Error).message ?? 'Unable to update AVAC submissions.', undefined, { duration: 3000 });
    } finally {
      this.setAvacToggleLoading(family.id, false);
    }
  }

  isBudgetToggleLoading(familyId: string | undefined) {
    if (!familyId) {
      return false;
    }
    return this.budgetToggleLoading()[familyId] ?? false;
  }

  async toggleBudgeting(family: FamilySummaryDto, enabled: boolean) {
    if (!family.id) {
      return;
    }
    this.setBudgetToggleLoading(family.id, true);
    try {
      await this.familyService.updateBudgetingEnabled(family.id, enabled);
      this.snackBar.open(
        enabled ? 'Budgeting is now active for this family.' : 'Budgeting has been disabled.',
        undefined,
        { duration: 2500 }
      );
    } catch (error) {
      this.snackBar.open((error as Error).message ?? 'Unable to update budgeting.', undefined, { duration: 3000 });
    } finally {
      this.setBudgetToggleLoading(family.id, false);
    }
  }

  isRecipeToggleLoading(familyId: string | undefined) {
    if (!familyId) {
      return false;
    }
    return this.recipeToggleLoading()[familyId] ?? false;
  }

  async toggleRecipes(family: FamilySummaryDto, enabled: boolean) {
    if (!family.id) {
      return;
    }
    this.setRecipeToggleLoading(family.id, true);
    try {
      await this.familyService.updateRecipesEnabled(family.id, enabled);
      this.snackBar.open(enabled ? 'Recipes are now enabled.' : 'Recipes have been disabled.', undefined, { duration: 2500 });
    } catch (error) {
      this.snackBar.open((error as Error).message ?? 'Unable to update recipes.', undefined, { duration: 3000 });
    } finally {
      this.setRecipeToggleLoading(family.id, false);
    }
  }

  isSleepTrackingToggleLoading(familyId: string | undefined) {
    if (!familyId) {
      return false;
    }
    return this.sleepTrackingToggleLoading()[familyId] ?? false;
  }

  async toggleSleepTracking(family: FamilySummaryDto, enabled: boolean) {
    if (!family.id) {
      return;
    }
    this.setSleepTrackingToggleLoading(family.id, true);
    try {
      await this.familyService.updateSleepTrackingEnabled(family.id, enabled);
      this.snackBar.open(
        enabled ? 'Sleep tracking is now active.' : 'Sleep tracking has been disabled.',
        undefined,
        { duration: 2500 }
      );
    } catch (error) {
      this.snackBar.open((error as Error).message ?? 'Unable to update sleep tracking.', undefined, { duration: 3000 });
    } finally {
      this.setSleepTrackingToggleLoading(family.id, false);
    }
  }

  private async loadJoinRequests(familyId: string | undefined) {
    if (!familyId) {
      return;
    }
    this.joinRequestsLoading.set(true);
    try {
      const requests = await this.familyService.getJoinRequests(familyId);
      this.joinRequests.set(requests);
      const selections = { ...this.requestRoleSelections() };
      for (const request of requests) {
        if (!request.id) {
          continue;
        }
        selections[request.id] = selections[request.id] ?? 'kid';
      }
      this.requestRoleSelections.set(selections);
    } catch {
      this.snackBar.open('Unable to load join requests. Try again later.', undefined, { duration: 3000 });
      this.joinRequests.set([]);
    } finally {
      this.joinRequestsLoading.set(false);
    }
  }

  private setAvacToggleLoading(familyId: string, value: boolean) {
    this.avacToggleLoading.update((current) => ({ ...current, [familyId]: value }));
  }

  private setBudgetToggleLoading(familyId: string, value: boolean) {
    this.budgetToggleLoading.update((current) => ({ ...current, [familyId]: value }));
  }

  private setRecipeToggleLoading(familyId: string, value: boolean) {
    this.recipeToggleLoading.update((current) => ({ ...current, [familyId]: value }));
  }

  private setSleepTrackingToggleLoading(familyId: string, value: boolean) {
    this.sleepTrackingToggleLoading.update((current) => ({ ...current, [familyId]: value }));
  }

  close() {
    this.dialogRef.close();
  }
}
